import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { API } from "@/config";
import { PawPrint, Loader2 } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState("Autenticazione in corso...");
  const [error, setError] = useState(null);

  useEffect(() => {
    // Use useRef to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    
    // Also check localStorage to prevent processing across remounts
    const processingKey = 'oauth_processing';
    const currentUrl = window.location.href;
    const lastProcessed = sessionStorage.getItem(processingKey);
    
    if (lastProcessed === currentUrl) {
      console.log('[AuthCallback] Already processed this URL, skipping');
      return;
    }
    
    hasProcessed.current = true;
    sessionStorage.setItem(processingKey, currentUrl);

    const processAuth = async () => {
      console.log('[AuthCallback] Starting auth process');
      console.log('[AuthCallback] Full URL:', window.location.href);
      console.log('[AuthCallback] Hash:', window.location.hash);
      console.log('[AuthCallback] Search:', window.location.search);
      
      // Try multiple ways to get session_id
      let sessionId = null;
      
      // 1. Try URL hash fragment (#session_id=...)
      const hash = window.location.hash;
      const hashMatch = hash.match(/session_id=([^&]+)/);
      if (hashMatch) {
        // Decode the session_id in case it's URL-encoded
        sessionId = decodeURIComponent(hashMatch[1]);
        console.log('[AuthCallback] Found session_id in hash, length:', sessionId.length);
      }
      
      // 2. Try query params (?session_id=...)
      if (!sessionId) {
        const paramValue = searchParams.get('session_id');
        if (paramValue) {
          sessionId = decodeURIComponent(paramValue);
          console.log('[AuthCallback] Found session_id in query params, length:', sessionId.length);
        }
      }
      
      // 3. Try full URL search
      if (!sessionId) {
        const urlMatch = window.location.href.match(/session_id=([^&\#]+)/);
        if (urlMatch) {
          sessionId = urlMatch[1];
          console.log('[AuthCallback] Found session_id in full URL, length:', sessionId.length);
        }
      }
      
      if (!sessionId) {
        console.error('[AuthCallback] No session_id found in URL:', window.location.href);
        setError("Nessun token di sessione trovato. Riprova.");
        setTimeout(() => navigate('/', { replace: true }), 3000);
        return;
      }

      console.log('[AuthCallback] session_id preview:', sessionId.substring(0, 20) + '...');
      setStatus("Verifica credenziali...");

      try {
        console.log('[AuthCallback] Calling backend API:', `${API}/auth/session`);
        
        // Exchange session_id for user data
        const response = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        console.log('[AuthCallback] Backend response status:', response.status);
        
        const responseData = await response.json();
        console.log('[AuthCallback] Backend response:', JSON.stringify(responseData).substring(0, 200));

        if (response.ok && responseData.user_id) {
          setStatus("Accesso completato!");
          console.log('[AuthCallback] Login successful for user:', responseData.email);
          
          // Clear processing flag
          sessionStorage.removeItem('oauth_processing');
          
          // Save session token
          if (responseData.session_token) {
            localStorage.setItem('session_token', responseData.session_token);
            console.log('[AuthCallback] Session token saved to localStorage');
          }
          
          // Login user
          login(responseData);
          
          // Clear the URL and navigate to dashboard
          window.history.replaceState(null, '', '/dashboard');
          
          // Small delay to show success message
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 500);
        } else {
          console.error('[AuthCallback] Session exchange failed:', responseData);
          // Clear processing flag to allow retry
          sessionStorage.removeItem('oauth_processing');
          // Show the specific error from backend
          const errorMessage = responseData.detail || "La sessione è scaduta. Riprova l'accesso con Google.";
          setError(errorMessage);
          setTimeout(() => navigate('/', { replace: true }), 4000);
        }
      } catch (error) {
        console.error('[AuthCallback] Auth callback error:', error);
        // Clear processing flag to allow retry
        sessionStorage.removeItem('oauth_processing');
        setError("Problema di connessione. Verifica la tua rete e riprova l'accesso.");
        setTimeout(() => navigate('/', { replace: true }), 4000);
      }
    };

    processAuth();
  }, [navigate, login, searchParams]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[#FFF8F5] to-white overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#FF6B4A]/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#FF8F6B]/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>
      
      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Animated logo container */}
        <div className="relative mb-8">
          {/* Outer ring animation */}
          {!error && (
            <div className="absolute inset-0 -m-4">
              <div className="w-28 h-28 rounded-full border-4 border-[#FF6B4A]/20 animate-ping" style={{ animationDuration: '2s' }} />
            </div>
          )}
          
          {/* Logo with pulse effect */}
          <div className={`relative w-24 h-24 rounded-3xl bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center shadow-xl ${!error ? 'animate-pulse' : ''}`} style={{ animationDuration: '2s' }}>
            <PawPrint className="text-white drop-shadow-lg" size={48} strokeWidth={2.5} />
            
            {/* Success/Error indicator */}
            {error ? (
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-white text-lg font-bold">!</span>
              </div>
            ) : (
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-[#FF6B4A]">
                <Loader2 className="w-5 h-5 text-[#FF6B4A] animate-spin" />
              </div>
            )}
          </div>
        </div>
        
        {/* Status text with fade-in effect */}
        <h1 className={`text-2xl font-semibold mb-2 ${error ? 'text-red-500' : 'text-[#1D1D1F]'} animate-fade-in`}>
          {error ? 'Oops!' : status}
        </h1>
        
        {/* Subtitle */}
        <p className={`text-base ${error ? 'text-red-400' : 'text-[#86868B]'} text-center max-w-xs`}>
          {error || 'Stiamo preparando il tuo account Zampa...'}
        </p>
        
        {/* Animated progress bar */}
        {!error && (
          <div className="mt-8 w-48 h-1.5 bg-[#FF6B4A]/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#FF6B4A] to-[#FF8F6B] rounded-full animate-progress"
              style={{
                animation: 'progress 2s ease-in-out infinite'
              }}
            />
          </div>
        )}
        
        {/* Error action button */}
        {error && (
          <button
            onClick={() => navigate('/', { replace: true })}
            className="mt-6 px-6 py-2.5 bg-[#FF6B4A] text-white font-medium rounded-full hover:bg-[#E65A3A] transition-colors shadow-lg hover:shadow-xl"
          >
            Torna alla Home
          </button>
        )}
      </div>
      
      {/* Custom CSS for animations */}
      <style>{`
        @keyframes progress {
          0% {
            width: 0%;
            margin-left: 0%;
          }
          50% {
            width: 60%;
            margin-left: 20%;
          }
          100% {
            width: 0%;
            margin-left: 100%;
          }
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}
